import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  CalendarDays, 
  Users, 
  Utensils, 
  Dumbbell, 
  LineChart, 
  Settings,
  Menu,
  X,
  Receipt,
  Laptop,
  UserCheck,
  LogOut
} from "lucide-react";
import { cn } from "@/lib/utils";
import { clientApi } from "@/services/api";
import { getLatestBmrUpdate, needsDietReview } from "@/lib/dietReview";

type SidebarItemProps = {
  icon: React.ElementType;
  label: string;
  to: string;
  isActive: boolean;
  showLabel: boolean;
  showDot?: boolean;
};

const SidebarItem = ({ icon: Icon, label, to, isActive, showLabel, showDot }: SidebarItemProps) => {
  return (
    <Link to={to} className="w-full">
      <div
        className={cn(
          "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 overflow-hidden",
          isActive 
            ? "bg-nutrition-green-100 text-nutrition-green-600" 
            : "hover:bg-nutrition-gray-100"
        )}
      >
        <span className="relative shrink-0">
          <Icon size={20} className={isActive ? "text-nutrition-green-500" : "text-gray-500"} />
          {showDot && !showLabel && (
            <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-red-500" />
          )}
        </span>
        {showLabel && (
          <span className="font-medium whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
        )}
        {showDot && showLabel && (
          <span className="ml-auto h-2.5 w-2.5 rounded-full bg-red-500 shrink-0" />
        )}
      </div>
    </Link>
  );
};

export function Sidebar() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const showLabels = isOpen;
  // True when at least one client's BMR review is overdue (red dot on Clients).
  const [hasOverdueClient, setHasOverdueClient] = useState(false);

  useEffect(() => {
    let active = true;
    clientApi.getAll()
      .then((clients) => {
        if (!active) return;
        const overdue = clients.some((client) =>
          needsDietReview(getLatestBmrUpdate(client.diet), client.dietReviewedAt)
        );
        setHasOverdueClient(overdue);
      })
      .catch(() => { /* non-critical badge */ });
    return () => { active = false; };
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("doctorSystemAuth");
    window.location.href = "/";
  };

  const menuItems = [
    { icon: CalendarDays, label: "Dashboard", path: "/LODYdashboard" },
    { icon: Users, label: "Clients", path: "/LODYClients" },
    { icon: Utensils, label: "Diet Plans", path: "/LODYDietPlans" },
    { icon: Dumbbell, label: "Workouts", path: "/LODYWorouts" },
    { icon: LineChart, label: "Analytics", path: "/LODYAnalytics" },
    { icon: Receipt, label: "Finance", path: "/LODYFinance" },
    { icon: Settings, label: "Exams", path: "/LODYSettings" },
    { icon: UserCheck, label: "Accounts", path: "/LODYAccounts" },
  ];

  return (
    <>
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 bg-white rounded-md shadow-md"
        >
          {isOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <div className={cn(
        "fixed top-0 left-0 h-full bg-white shadow-md z-40 transition-all duration-300 overflow-hidden",
        isOpen ? "w-64" : "w-0 -translate-x-full lg:w-16 lg:translate-x-0",
        "lg:relative lg:block"
      )}>
        <div className="p-4">
          <h2 className={cn(
            "text-xl font-bold text-nutrition-green-600 mb-8 mt-4",
            !isOpen && "lg:text-center"
          )}>
            {isOpen ? "NUVAMAJ" : "Nuvamaj"}
          </h2>

          <nav className="space-y-1">
            {menuItems.map((item) => (
              <SidebarItem
                key={item.path}
                icon={item.icon}
                label={item.label}
                to={item.path}
                isActive={location.pathname === item.path}
                showLabel={showLabels}
                showDot={item.path === "/LODYClients" && hasOverdueClient}
              />
            ))}
            
            {/* Logout Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 w-full text-red-600 hover:bg-red-50",
                  !isOpen && "lg:justify-center"
                )}
              >
                <LogOut size={20} />
                {showLabels && <span className="font-medium">Logout</span>}
              </button>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
